import { LitElement, html } from "lit";
import { customElement, state } from "lit/decorators.js";

import { rpc } from "ui-common/services/rpc";
import { isLocalFeatureEnabled } from "helpers/local-feature-flags";
import "ui-components/button/wow-button.ts";
import "../components/wow-logo-full/wow-logo-full";

import { t, translate } from "../translations/i18n";
import { styles } from "./verify-purchase.css";

@customElement("verify-purchase-page")
export class VerifyPurchasePage extends LitElement {
  static override styles = [styles];

  @state()
  private email = "";

  @state()
  private eventHandle = "";

  @state()
  private eventId = "";

  @state()
  private isResending = false;

  @state()
  private resendSuccess = false;

  @state()
  private resendError = "";

  @state()
  private initialEmailSent = false;

  override connectedCallback() {
    super.connectedCallback();

    if (!isLocalFeatureEnabled("guest-purchase-flow")) {
      window.location.href = "/my/";
      return;
    }

    this.extractUrlParams();
    this.sendInitialVerificationEmail();
  }

  private extractUrlParams() {
    const urlParams = new URLSearchParams(window.location.search);
    this.email = urlParams.get("email") || "";
    this.eventHandle = urlParams.get("eventHandle") || "";
    this.eventId = urlParams.get("eventId") || "";
  }

  private async sendInitialVerificationEmail() {
    if (!this.email || !this.eventHandle || !this.eventId || this.initialEmailSent) {
      return;
    }

    this.initialEmailSent = true;

    try {
      await rpc("resend-purchase-verification-email", {
        email: this.email,
        eventHandle: this.eventHandle,
        eventId: this.eventId,
      });
    } catch (error) {
      console.error("Failed to send initial verification email:", error);
    }
  }

  private async handleResendEmail() {
    if (this.isResending || !this.email || !this.eventHandle || !this.eventId) {
      return;
    }

    this.isResending = true;
    this.resendSuccess = false;
    this.resendError = "";

    try {
      await rpc("resend-purchase-verification-email", {
        email: this.email,
        eventHandle: this.eventHandle,
        eventId: this.eventId,
      });
      this.resendSuccess = true;
    } catch (error) {
      console.error("Failed to resend verification email:", error);
      this.resendError = translate("verifyPurchase.emailError");
    } finally {
      this.isResending = false;
    }
  }

  protected override render() {
    return html`
      <div class="container">
        <div class="logo">
          <wow-logo-full></wow-logo-full>
        </div>

        <div class="content">
          <div class="success-header">
            <h1 class="title">${t("verifyPurchase.headerSuccess")}</h1>
            <p class="subtitle">${t("verifyPurchase.ticketSecured")}</p>
          </div>

          <div class="verification-section">
            <h2 class="section-title">${t("verifyPurchase.completeStep")}</h2>
            <p class="section-text">${t("verifyPurchase.toAccessLater")}</p>
            <ul class="requirements-list">
              <li>${t("verifyPurchase.verifyEmail")}</li>
              <li>${t("verifyPurchase.setPassword")}</li>
            </ul>
            <p class="quick-note">${t("verifyPurchase.quickProcess")}</p>
          </div>

          <div class="actions">
            <wow-button variant="primary" size="medium" full-width disabled>
              ${t("verifyPurchase.verifyButton")}
            </wow-button>

            <wow-button
              variant="secondary"
              size="medium"
              full-width
              @click=${this.handleResendEmail}
              ?disabled=${this.isResending}
            >
              ${this.isResending ? t("verifyPurchase.emailSending") : t("verifyPurchase.resendButton")}
            </wow-button>
          </div>

          ${this.resendSuccess ? html`<p class="success-message">${t("verifyPurchase.emailSent")}</p>` : ""}
          ${this.resendError ? html`<p class="error-message">${this.resendError}</p>` : ""}

          <div class="email-info">
            <p class="email-sent-text">
              ${translate("verifyPurchase.emailSentTo").replace("{email}", this.email || "your email")}
            </p>
            <p class="spam-note">${t("verifyPurchase.spamNote")}</p>
          </div>

          <p class="support-note">${t("verifyPurchase.supportNote")}</p>
        </div>
      </div>
    `;
  }
}
